<script lang="ts">
  import { onMount } from 'svelte';
  import { formListStore } from '@entities/form/store.svelte';
  import { formSubmissionListStore } from '@entities/form-submission/store.svelte';
  import { navigate } from '@shared/router/navigation';
  import type {
    ApplicantType,
    FormSubmission,
    FormSubmissionCandidatePresence,
    FormSubmissionQuery,
    SubmissionMatchingStatus,
  } from '@shared/types/form-submission';
  import { formatDateTime } from '@shared/util/format';

  const limit = 50;

  let formId = $state('');
  let matchingStatus = $state('');
  let applicantType = $state('');
  let candidatePresence = $state('');
  let offset = $state(0);

  let formOptions = $derived(formListStore.data ?? []);
  let canGoNext = $derived((formSubmissionListStore.data?.length ?? 0) === limit);
  let canGoPrev = $derived(offset > 0);

  function statusLabel(status: SubmissionMatchingStatus): string {
    if (status === 'unmatched') return '未紐付け';
    if (status === 'auto_matched') return '自動紐付け';
    if (status === 'needs_review') return '要確認';
    if (status === 'confirmed') return '確定';
    if (status === 'spam') return 'スパム';
    return '破棄';
  }

  function statusTagClass(status: SubmissionMatchingStatus): string {
    if (status === 'confirmed' || status === 'auto_matched') return 'tag tag-ok';
    if (status === 'spam' || status === 'discarded') return 'tag tag-danger';
    if (status === 'needs_review') return 'tag tag-warn';
    return 'tag';
  }

  function applicantLabel(value: ApplicantType): string {
    if (value === 'lawyer') return '弁護士';
    if (value === 'staff') return '事務局';
    if (value === 'other_professional') return 'その他士業';
    return '不明';
  }

  function actionLabel(action: string): string {
    if (action === 'review_firm_candidates') return '候補確認';
    if (action === 'review_submission') return '内容確認';
    if (action === 'confirm_or_discard') return '紐付け/破棄';
    if (action === 'review_field_update_proposal') return '更新案確認';
    if (action === 'restore_if_needed') return '復元確認';
    return '対応なし';
  }

  function formLabel(id: string): string {
    return formOptions.find((form) => form.id === id)?.name ?? id;
  }

  function buildQuery(): FormSubmissionQuery {
    return {
      form_id: formId || undefined,
      matching_status: (matchingStatus || undefined) as SubmissionMatchingStatus | undefined,
      applicant_type: (applicantType || undefined) as ApplicantType | undefined,
      candidate_presence: (candidatePresence || undefined) as
        | FormSubmissionCandidatePresence
        | undefined,
      limit,
      offset,
    };
  }

  async function refresh(): Promise<void> {
    await formSubmissionListStore.refresh(buildQuery());
  }

  function applyFilters(): void {
    offset = 0;
    void refresh();
  }

  function changePage(direction: -1 | 1): void {
    const nextOffset = offset + direction * limit;
    if (nextOffset < 0) return;
    offset = nextOffset;
    void refresh();
  }

  function detailPath(submission: FormSubmission): string {
    return `/form-submissions/${submission.id}`;
  }

  function handleLinkClick(ev: MouseEvent, path: string): void {
    if (ev.metaKey || ev.ctrlKey || ev.shiftKey || ev.altKey || ev.button !== 0) return;
    ev.preventDefault();
    navigate(path);
  }

  onMount(() => {
    void formListStore.refresh();
    void refresh();
  });
</script>

<header class="page-head">
  <div>
    <h1>フォーム受信トレイ</h1>
  </div>
</header>

<section class="panel filters" aria-label="フォーム送信フィルター">
  <label>
    <span>フォーム</span>
    <select bind:value={formId} onchange={applyFilters}>
      <option value="">すべて</option>
      {#each formOptions as form (form.id)}
        <option value={form.id}>{form.name}</option>
      {/each}
    </select>
  </label>
  <label>
    <span>状態</span>
    <select bind:value={matchingStatus} onchange={applyFilters}>
      <option value="">すべて</option>
      <option value="needs_review">要確認</option>
      <option value="unmatched">未紐付け</option>
      <option value="auto_matched">自動紐付け</option>
      <option value="confirmed">確定</option>
      <option value="spam">スパム</option>
      <option value="discarded">破棄</option>
    </select>
  </label>
  <label>
    <span>申込者</span>
    <select bind:value={applicantType} onchange={applyFilters}>
      <option value="">すべて</option>
      <option value="lawyer">弁護士</option>
      <option value="staff">事務局</option>
      <option value="other_professional">その他士業</option>
      <option value="unknown">不明</option>
    </select>
  </label>
  <label>
    <span>候補</span>
    <select bind:value={candidatePresence} onchange={applyFilters}>
      <option value="">すべて</option>
      <option value="with_candidates">候補あり</option>
      <option value="without_candidates">候補なし</option>
    </select>
  </label>
  <button type="button" class="btn" onclick={() => void refresh()}>
    更新
  </button>
</section>

<section class="panel">
  {#if formSubmissionListStore.loading && formSubmissionListStore.data === null}
    <p class="muted">読み込み中...</p>
  {:else if formSubmissionListStore.error}
    <p class="error" role="alert">{formSubmissionListStore.error}</p>
  {:else if formSubmissionListStore.data && formSubmissionListStore.data.length > 0}
    <div class="table-scroll">
      <table>
        <thead>
          <tr>
            <th>受信日時</th>
            <th>状態</th>
            <th>次アクション</th>
            <th>フォーム</th>
            <th>申込者</th>
            <th>候補</th>
            <th>紐付け先</th>
          </tr>
        </thead>
        <tbody>
          {#each formSubmissionListStore.data as submission (submission.id)}
            <tr>
              <td class="timestamp">
                <a
                  href={detailPath(submission)}
                  onclick={(ev) => handleLinkClick(ev, detailPath(submission))}
                >
                  {formatDateTime(submission.received_at)}
                </a>
                <div class="row-sub">{submission.id}</div>
              </td>
              <td>
                <span class={statusTagClass(submission.matching_status)}>
                  {statusLabel(submission.matching_status)}
                </span>
              </td>
              <td>{actionLabel(submission.reviewer_next_action)}</td>
              <td class="form-cell">{formLabel(submission.form_id)}</td>
              <td>
                {applicantLabel(submission.applicant_type)}
                {#if submission.applicant_type_reason}
                  <div class="row-sub">{submission.applicant_type_reason}</div>
                {/if}
              </td>
              <td>{submission.firm_candidate_count}件</td>
              <td class="match-cell">
                {#if submission.matched_firm_id}
                  <code>{submission.matched_firm_id}</code>
                  {#if submission.matched_lawyer_id}
                    <div class="row-sub">{submission.matched_lawyer_id}</div>
                  {/if}
                {:else}
                  <span class="muted">未設定</span>
                {/if}
              </td>
            </tr>
          {/each}
        </tbody>
      </table>
    </div>
    <div class="pager">
      <button type="button" class="btn" disabled={!canGoPrev} onclick={() => changePage(-1)}>
        前へ
      </button>
      <span>{offset + 1} - {offset + formSubmissionListStore.data.length}</span>
      <button type="button" class="btn" disabled={!canGoNext} onclick={() => changePage(1)}>
        次へ
      </button>
    </div>
  {:else}
    <p class="empty">該当する送信はありません。</p>
  {/if}
</section>

<style>
  .page-head {
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 12px;
    margin: 0 0 12px;
    flex-wrap: wrap;
  }
  .page-head h1 {
    margin: 0;
    font-size: 1.25rem;
  }
  .filters {
    display: flex;
    align-items: end;
    gap: 10px;
    flex-wrap: wrap;
  }
  .filters label {
    display: grid;
    gap: 4px;
    min-width: 180px;
  }
  .filters label:first-child {
    min-width: 260px;
  }
  .filters span {
    color: var(--muted);
    font-size: 12px;
    font-weight: 700;
  }
  .timestamp,
  .match-cell {
    min-width: 190px;
  }
  .form-cell {
    min-width: 180px;
  }
  .row-sub {
    margin-top: 2px;
    color: var(--muted);
    font-size: 11px;
    overflow-wrap: anywhere;
  }
  .pager {
    display: flex;
    justify-content: flex-end;
    align-items: center;
    gap: 10px;
    margin-top: 12px;
  }
  code {
    overflow-wrap: anywhere;
    font-family: ui-monospace, SFMono-Regular, Menlo, monospace;
    font-size: 12px;
  }
  button:disabled {
    opacity: 0.6;
    cursor: not-allowed;
  }
</style>
